/**
 * "Add to the suppression list" as a CRM dialog.
 *
 * Was an inline panel that unfolded above the list and pushed it down, which is
 * the pre-redesign habit this pass removes: the list is the page, and a form
 * that shoves it out of the way is a dialog that has not admitted it yet.
 *
 * Everything added here is added as `manual`, and that is the only kind of row
 * that can be taken off again. A row the recipient created - an unsubscribe, a
 * complaint - is permanent, so this form deliberately cannot create one: a
 * person typing an address in by hand should not be able to make it
 * unremovable by picking the wrong item in a dropdown.
 */
import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { addSuppression } from "../../../../lib/marketing";
import { Btn, Modal, SelectInput } from "../../../ui";

type Kind = "email" | "domain";

export function AddSuppressionModal({
  onClose,
  onAdded,
  onError,
}: {
  onClose: () => void;
  onAdded: () => void;
  onError: () => void;
}) {
  const t = useT();
  const [kind, setKind] = useState<Kind>("email");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const trimmed = value.trim();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmed) return;
    setBusy(true);
    try {
      await addSuppression({ kind, value: trimmed, ...(note.trim() ? { note: note.trim() } : {}) });
      onAdded();
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("marketing.suppression.add")}
      label={t("marketing.suppression.add")}
      onClose={onClose}
      className="max-w-[460px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("marketing.suppression.col.value")} *</span>
          <SelectInput
            value={kind}
            onChange={(next) => setKind(next as Kind)}
            label={t("marketing.suppression.col.value")}
          >
            <option value="email">{t("marketing.suppression.kind.email")}</option>
            <option value="domain">{t("marketing.suppression.kind.domain")}</option>
          </SelectInput>
        </div>

        <div>
          {/* The placeholder changes with the kind, because "company.ee" and
              "name@company.ee" are both plausible entries and only one of them
              is what the selected kind means. */}
          <label className="crm-inp">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t(
                kind === "email"
                  ? "marketing.suppression.emailPlaceholder"
                  : "marketing.suppression.domainPlaceholder",
              )}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
            />
          </label>
        </div>

        <div>
          <span className="crm-lab">{t("marketing.suppression.col.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("marketing.suppression.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || !trimmed}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t("marketing.suppression.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
